import { useState } from "react";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useActiveOrganizationRole } from "@hooks/permissions/organization/useActiveOrganizationRole";
import { useUser } from "@hooks/useUser";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useCanManageOrganizationMember } from "@hooks/permissions/organization/useCanManageOrganizationMember";
import { useRemoveOrganizationMemberMutation } from "@api/organizationMember.api";
import { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { useOverlays } from "contexts/OverlayContext";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { TransferOrganizationOwnerModal } from "./TransferOrganizationOwnerModal";

interface MemberActionsCellProps {
    member: OrganizationMember & { type: "member"; id: string | number };
}

export const MemberActionsCell = ({ member }: MemberActionsCellProps) => {
    const activeOrganization = useActiveOrganization();
    const actingUserRole = useActiveOrganizationRole();
    const currentUser = useUser();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const { openOverlay } = useOverlays();

    const canManageMember = useCanManageOrganizationMember(member);
    const removeMember = useRemoveOrganizationMemberMutation(
        member,
        activeOrganization!,
    );

    const currentUserIsOwner = actingUserRole === OrganizationUserRole.OWNER;
    const targetIsNotCurrentUser = member.user.id !== currentUser?.id;
    const targetIsAdmin = member.role === OrganizationUserRole.ADMIN;

    const canTransferOwner =
        currentUserIsOwner && targetIsNotCurrentUser && targetIsAdmin;

    if (!canManageMember && !canTransferOwner) {
        return null;
    }

    return (
        <>
            <Menu.More className={tableStyles.showOnHover}>
                {canManageMember && (
                    <Menu.Item
                        onClick={() => {
                            if (!activeOrganization) {
                                return;
                            }
                            openOverlay(
                                "organizationConfigureMemberPermissions",
                                {
                                    organization: activeOrganization,
                                    member,
                                },
                            );
                        }}
                    >
                        Configure permissions
                    </Menu.Item>
                )}

                {canTransferOwner && (
                    <Menu.Item onClick={() => setShowTransferModal(true)}>
                        Transfer owner role
                    </Menu.Item>
                )}

                {canManageMember && (
                    <Menu.Item
                        destructive
                        onClick={() => setShowDeleteConfirmation(true)}
                    >
                        Remove from organization
                    </Menu.Item>
                )}
            </Menu.More>

            <ConfirmationModal
                show={showDeleteConfirmation}
                title={`Are you sure you want to remove ${member.user.name || member.user.email} from the organization?`}
                question=""
                onConfirm={async () => {
                    await removeMember.mutateAsync();
                    setShowDeleteConfirmation(false);
                }}
                no="Cancel"
                yes="Remove"
                onReject={() => setShowDeleteConfirmation(false)}
            />

            {canTransferOwner && (
                <TransferOrganizationOwnerModal
                    member={member}
                    show={showTransferModal}
                    onHide={() => setShowTransferModal(false)}
                />
            )}
        </>
    );
};
